import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ITransaction } from '../interface/transaction';
import { useTransaction } from '../context/TransactionContext';
import { useLoading } from '../context/LoadingContext';
import { useLanguage } from '../context/LanguageContext';
import { useTransactionParameters } from '../context/TransactionParameterContext';
import { fetchMatterById } from './useFetchMatterById';
import { fetchTransactionTypeById } from './useFetchTransactionTypes';
import {
  buildMatterSelection,
  MATTER_SELECTION_FIELDS,
} from '../components/dashboard/addTransaction/matterSelection';

interface UseCopyTransactionOptions {
  setCopyClicked?: Dispatch<SetStateAction<boolean | undefined>>;
  setSelectedTransaction?: Dispatch<SetStateAction<ITransaction | undefined>>;
}

export const useCopyTransaction = ({
  setCopyClicked,
  setSelectedTransaction,
}: UseCopyTransactionOptions = {}) => {
  const { updateTransaction } = useTransaction();
  const queryClient = useQueryClient();
  const { setIsLoading } = useLoading();
  const { getAppLanguageCode } = useLanguage();
  const { parameters } = useTransactionParameters();
  const transactionTypeInUse = parameters?.transactionTypeInUse;

  // The source transaction is selected so its matter's restrictions are checked before the form opens.
  const openAsNewTransaction = useCallback(
    (newTransaction: ITransaction, source: ITransaction) => {
      updateTransaction(newTransaction);
      setCopyClicked?.(true);
      setSelectedTransaction?.(source);
    },
    [setCopyClicked, setSelectedTransaction, updateTransaction],
  );

  const copyTransaction = useCallback(
    (transaction: ITransaction) => {
      openAsNewTransaction(
        {
          ...transaction,
          id: undefined,
          overrideWarnings: true,
          shouldLoadServerPrice: true,
          currentSubject: transaction?.subject,
          transactionDate: dayjs().format('YYYY-MM-DD'),
          isCopiedTransaction: true,
        } as unknown as ITransaction,
        transaction,
      );
    },
    [openAsNewTransaction],
  );

  // A listed transaction's matter lacks the client and default transaction type a picked matter brings, so both are fetched.
  const loadTransactionOnSameMatter = useCallback(
    async (matterId: string) => {
      const matter = await queryClient.fetchQuery({
        queryKey: ['matter-for-new-transaction', matterId],
        queryFn: () => fetchMatterById({ id: matterId, ...MATTER_SELECTION_FIELDS }),
      });
      const defaultTransactionType =
        transactionTypeInUse && matter.defaultTransactionTypeId
          ? await queryClient.fetchQuery({
              queryKey: ['transaction-types', matter.defaultTransactionTypeId],
              queryFn: () => fetchTransactionTypeById(matter.defaultTransactionTypeId),
            })
          : null;
      return buildMatterSelection(null, matter, {
        defaultTransactionType,
        transactionTypeInUse,
        appLanguage: getAppLanguageCode(),
      });
    },
    [getAppLanguageCode, queryClient, transactionTypeInUse],
  );

  const startTransactionOnSameMatter = useCallback(
    (transaction: ITransaction) => {
      setIsLoading(true);
      loadTransactionOnSameMatter(transaction.matterId)
        .then((newTransaction) => openAsNewTransaction(newTransaction, transaction))
        .catch(() => {
          // The global API error handler has already shown why the form did not open.
        })
        .finally(() => setIsLoading(false));
    },
    [loadTransactionOnSameMatter, openAsNewTransaction, setIsLoading],
  );

  return { copyTransaction, startTransactionOnSameMatter };
};
